<template>
  <component is="style" v-if="model.state.elementStyling?.cssStyle" type="text/css">
    {{ model.state.elementStyling.cssStyle }}
  </component>

  <div
    class="registration-form-wrapper"
    :style="state?.elementStyling?.container"
    :class="{ 'not-valid-settings': !model.isAddonValid() }"
  >
    <Flow>
      <FlowPage
        v-if="!showConfirmation && !messageState"
        key="form"
        :animation-enter="defaultEnterAnimation"
        :animation-leave="leaveAnimation"
      >
        <div
          class="registration-form"
          :class="{
            'registration-form--inline-messages': state.appearance?.formStyle?.validationMessageType === 'inline'
          }"
          :style="state.elementStyling?.inline"
        >
          <div class="row">
            <div class="col">
              <div class="container">
                <form
                  :id="formName"
                  ref="formRef"
                  :data-form-id="formName"
                  class="content__item content__item--form"
                  :autocomplete="state.disableFormAutoComplete ? 'off' : ''"
                  :class="{
                    'content__item--form-autofilled': hasAutofilled,
                    invalid: !isValid,
                    valid: isValid,
                    error: isError
                  }"
                  @submit.prevent="submitForm"
                >
                  <div class="row">
                    <FormElement
                      v-for="formField in fields"
                      :key="formField.id"
                      :model="formField"
                      :show-validation-errors="didSubmit"
                    />
                  </div>
                </form>
              </div>
            </div>
          </div>
          <div v-if="showButton" class="row">
            <div class="col">
              <div
                class="content__item content__item--action container"
                :style="state.elementStyling?.buttonContainerStyle"
              >
                <div class="row">
                  <div class="col">
                    <UIButton :is-loading="isLoading" :type="state.button?.type" @click="submitForm">
                      <!-- nosem -->
                      <span class="content__item-action-text" v-html="state.button?.label || 'Send'"></span>
                    </UIButton>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </FlowPage>

      <FlowPage v-else-if="messageState" key="message">
        <div class="content__item content__item--text">
          <div class="row row--message row-expired row--expired-campaign" :style="messageState.inlineStyles">
            <h2 :style="messageState.inlineStyles">{{ messageState.headline ?? '' }}</h2>
            <div class="message-body" :style="messageState.inlineStyles">{{ messageState.description ?? '' }}</div>
          </div>
        </div>
      </FlowPage>

      <FlowPage v-else-if="showConfirmation" key="confirmation">
        <ApplyReplacementTags
          class="registration-confirmation"
          tag="div"
          :html="state?.confirmation?.message"
        ></ApplyReplacementTags>
      </FlowPage>
    </Flow>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, getCurrentInstance, nextTick, onBeforeUnmount, PropType, ref } from 'vue';
import { AddonRegistrationModel } from '@/components/addons/registration/Model';
import FormElement from '@/components/content/form/elements/FormElement.vue';
import useDevice from '@/hooks/useDevice';
import { useStore } from '@/store';
import {
  saveGlobalFormCookies,
  setFormReplacementTags,
  submitForm as submitRegistrationForm
} from '@/utilities/Registration';
import { SectionType } from '@/typings/enums/enums';
import Flow from '@/components/Flow/Flow.vue';
import FlowPage from '@/components/Flow/FlowPage.vue';
import { defaultEnterAnimation, defaultLeaveAnimation } from '@/components/Flow/Types';
import useFlow from '@/hooks/useFlow';
import { FormElementModel } from '@/components/addons/registration/types';
import { recursivelyWaitForPromises } from '@/utilities/virtualDom';
import { events } from '@/services/events';
import { FormSubmitEvent } from '@/events/formSubmit';
import { FormSubmitResponseEvent } from '@/events/formSubmitResponse';
import { ReplacementTags } from '@/store/modules/global/state';
import { redirect, RedirectTarget } from '@/services/url';
import UIButton from '@/components/ui/UIButton.vue';
import { FormDisableDefaultBehavior } from '@/exceptions/FormDisableDefaultBehavior';
import ApplyReplacementTags from '@/components/ApplyReplacementTags.vue';
import MessagesHandlerModel, { MessagesState } from '@/models/messages/MessagesHandlerModel';
import { MessageDisplayTypes } from '@/typings/interfaces/data/campaign';

export default defineComponent({
  name: 'AddonRegistration',
  components: {
    UIButton,
    FlowPage,
    Flow,
    FormElement,
    ApplyReplacementTags
  },
  props: {
    model: {
      type: Object as PropType<AddonRegistrationModel>,
      required: true
    }
  },
  setup(props) {
    props.model.state.fields?.forEach((field) => {
      field.setInitialValue();
    });

    const store = useStore();
    const model = props.model as AddonRegistrationModel;
    const state = model.state;
    const showButton = ref(true);
    const showConfirmation = ref(false);
    const hasAutofilled = ref(false);
    const isError = ref(false);
    const isLoading = ref(false);
    const formName = state.formId;
    const didSubmit = ref(false);
    const formRef = ref<HTMLElement | null>(null);
    let disallowFlowpageChange = false;

    const isValid = computed(() => {
      return fields.value.filter((field) => !field.state.isValid).length === 0 ?? true;
    });

    // Validation happens async. We need to ensure that we only allow people to submit when all has been validated.
    const isValidated = computed(() => {
      return fields.value.filter((field) => !field.state.validated).length === 0 ?? true;
    });

    if (model.getSection().getSectionType() === SectionType.FLOWPAGE) {
      showButton.value = false;
    }

    const submitForm = async () => {
      if (!didSubmit.value) {
        didSubmit.value = true;
      }

      if (isValid.value && isValidated.value) {
        isLoading.value = true;

        try {
          await submitRegistrationForm(model.getSection());
          setFormReplacementTags();
        } catch (e) {
          if (e instanceof FormDisableDefaultBehavior) {
            disallowFlowpageChange = !e.allowContinueInFlow;
          } else {
            // If exception wasn't the exception to disable default behavior we should bubble up the exception.
            throw e;
          }
        }
      }
    };

    const scrollToFormErrors = async () => {
      await nextTick();
      const form = formRef.value;
      if (form) {
        const error = form.querySelector('.form-group--error');
        error?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };

    const fields = computed<FormElementModel[]>(() => {
      const { isDesktop, isTablet, isMobile } = useDevice();

      return (
        state?.fields?.filter((field) => {
          const fieldState = field.state;
          const conditions = fieldState?.visibilityConditions;

          if (fieldState.deviceSettings && !fieldState.deviceSettings.showOnDesktop && isDesktop) {
            return false;
          }

          if (fieldState.deviceSettings && !fieldState.deviceSettings.showOnTablet && isTablet) {
            return false;
          }

          if (fieldState.deviceSettings && !fieldState.deviceSettings.showOnMobile && isMobile) {
            return false;
          }

          return conditions ? conditions.check() : true;
        }) ?? []
      );
    });

    const currentInstance = getCurrentInstance();
    let readyPromiseResolve: (() => void) | undefined;

    const readyPromise = new Promise<void>((resolve) => {
      readyPromiseResolve = resolve;
    });

    nextTick(async () => {
      if (currentInstance) {
        await recursivelyWaitForPromises(currentInstance.vnode, 'onBeforeEnter', 1);
      }

      await nextTick();

      let goToNextFlowPage = false;
      const campaignModel = store.getters['campaign/model'];

      if (model.state.skipRegistration && !campaignModel.state.isEditModeActive) {
        const section = model.getSection();

        if (
          (section.getSectionType() === SectionType.FLOWPAGE || section.getSectionType() === SectionType.SECTION) &&
          isValid.value
        ) {
          section.hide();
          goToNextFlowPage = true;

          await submitForm();
        }
      }

      if (readyPromiseResolve) {
        readyPromiseResolve();
      }

      if (goToNextFlowPage && !disallowFlowpageChange) {
        await nextTick();
        useFlow().goToNextFlowPage();
      }
    });

    const onFormSubmit = (event: FormSubmitEvent) => {
      if (event.detail === model.getSection()) {
        if (!didSubmit.value) {
          didSubmit.value = true;
        }

        if (!isValid.value || !isValidated.value) {
          event.preventDefault();
          scrollToFormErrors();
          return;
        }
        const replacementTags: ReplacementTags = {};

        state.fields?.forEach((field) => {
          replacementTags[`registration_field_${field.id}`] = field.getSerializedCookieValue();
        });

        store.dispatch('global/setReplacementTags', replacementTags);

        setFormReplacementTags();

        // Initiate save of global form cookies before saving the actual form.
        saveGlobalFormCookies(model.state.fields ?? []);
      }
    };

    const messageState = ref<MessagesState | undefined>();

    const onFormSubmitResponse = async (event: FormSubmitResponseEvent) => {
      const campaignModel = store.getters['campaign/model'];
      const campaignState = store.getters['campaign/state'];
      if (event.detail.pageModel !== model.getSection()) {
        return;
      }

      const response = event.detail.response;

      /**
       * Handle when we a game limit has been reached (eg: Number of registrations pr day)
       */
      if (response.error && response.message === 'game limit reached') {
        const messageModel = campaignState.messages.messageGameLimit;
        messageModel.trigger();
        event.preventDefault();
        return;
      }

      if (response.error && response.show_message) {
        const messageModel = new MessagesHandlerModel(response.show_message);

        const hasGameFlowAddon = campaignModel.state.sections.some(
          (section) => section.getAddons('gameflow').length > 0
        );

        if (!hasGameFlowAddon && messageModel.state.displayType === MessageDisplayTypes.INLINE_MESSAGE) {
          messageState.value = messageModel.state;
          const section = model.getSection();
          section.show();
        } else {
          messageModel.trigger();
        }
        event.preventDefault();
        return;
      }

      if (state.redirect && state.redirect.url) {
        redirect({
          url: state.redirect.url,
          target: RedirectTarget.SELF
        });
        return;
      }
      /**
       * Handle when we identify a user that is trying to cheat
       */
      if (response.object && response.object.token === '61c15628d7585e4aeb78e3ac0e8a7d35') {
        const messageModel = campaignState.messages.messageCheating;
        messageModel.trigger();
        event.preventDefault();
        return;
      }

      // Response can include a flow page that the handler should go-to instead of the default behavior.
      // So here we throw a prevent behavior exception for the default behavior.
      // This exception is then caught in the button addon which initiated the form submit.
      if (response.goto_flow_page) {
        const { makeSpecificFlowPageActive } = useFlow();
        makeSpecificFlowPageActive(response.goto_flow_page);
        event.preventDefault();
        return;
      }

      if (response.isError) {
        isError.value = true;
      } else {
        isLoading.value = false;

        if (model.getSection().getSectionType() !== SectionType.FLOWPAGE) {
          showConfirmation.value = true;
        }
      }
    };

    events.on('formSubmit', onFormSubmit);
    events.on('formSubmitResponse', onFormSubmitResponse);

    onBeforeUnmount(() => {
      events.off('formSubmit', onFormSubmit);
      events.off('formSubmitResponse', onFormSubmitResponse);
    });

    return {
      formRef,
      store,
      formName,
      isValid,
      hasAutofilled,
      state,
      showButton,
      didSubmit,
      submitForm,
      isLoading,
      isError,
      messageState,
      showConfirmation,
      fields,
      defaultEnterAnimation,
      leaveAnimation:
        model.getSection().getSectionType() !== SectionType.FLOWPAGE ? defaultLeaveAnimation : defaultLeaveAnimation,
      onBeforeEnter: async () => {
        await readyPromise;
      }
    };
  }
});
</script>

<style lang="scss" scoped>
.registration-form-wrapper {
  &.not-valid-settings {
    border: 0.2rem dashed hsla(0, 0%, 100%, 0.3);
    height: 100%;
  }
}
</style>

<style lang="scss">
.registration-form,
.registration-confirmation {
  .content__item--action {
    margin-bottom: 0;
  }
}
.grid__addon.grid__addon.grid__addon--registration,
.grid__addon.grid__addon--width-auto.grid__addon--registration,
.grid__addon.grid__addon--width-custom.grid__addon--registration {
  width: 100%;
}
</style>
